import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/* ⏱ Live countdown formatter */
function formatCountdown(ms) {
  if (ms == null) return "—";
  if (ms <= 0) return "Auction ended";

  const totalSeconds = Math.floor(ms / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
}

export default function LeadDetails() {
  const { estimateId } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openItemIndex, setOpenItemIndex] = useState(null);

  // ✅ vendor payment verification state
  const [hasVerifiedPayment, setHasVerifiedPayment] = useState(false);

  // ⏱ countdown state
  const [timeLeftMs, setTimeLeftMs] = useState(null);

  const token = localStorage.getItem("vendor_token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    async function loadLeadAndStatus() {
      try {
        // 1️⃣ Load lead
        const res = await fetch(
          `https://api.showmeyourjunk.biz/api/vendor/lead/${estimateId}`,
          { headers: { "x-vendor-token": token } }
        );

        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || "Failed to load lead");
        }

        setLead(data.lead);

        // Initialize countdown
        if (data.lead.auction_end_at) {
          const end = new Date(data.lead.auction_end_at);
          if (!isNaN(end.getTime())) {
            setTimeLeftMs(end - new Date());
          }
        }

        // 2️⃣ Load payment verification status
        const statusRes = await fetch(
          "https://api.showmeyourjunk.biz/api/vendor/payment-status",
          { headers: { "x-vendor-token": token } }
        );

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setHasVerifiedPayment(!!statusData.has_verified_payment);
        }

      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadLeadAndStatus();
  }, [estimateId, navigate, token]);

  // ⏱ Live countdown tick
  useEffect(() => {
    if (!lead?.auction_end_at) return;

    const end = new Date(lead.auction_end_at);
    if (isNaN(end.getTime())) return;

    const interval = setInterval(() => {
      setTimeLeftMs(end - new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [lead]);

  if (loading) return <div style={{ padding: 40 }}>Loading…</div>;
  if (error) return <div style={{ padding: 40 }}>{error}</div>;
  if (!lead) return <div style={{ padding: 40 }}>Lead not found</div>;

  const items = Array.isArray(lead.items) ? lead.items : [];
  const images = Array.isArray(lead.photos_json) ? lead.photos_json : [];

  // Some environments return pricing nested; others return fee fields at root.
  const pricing = lead.pricing || lead || {};

  const totalVolume = items.reduce(
    (sum, i) => sum + Number(i.est_volume_cuft || 0),
    0
  );

  const totalWeight = items.reduce(
    (sum, i) => sum + Number(i.est_weight_lbs || 0),
    0
  );

  function safeNum(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  function parseDimensions(dimensions) {
    if (!dimensions) return { width: 0, height: 0, depth: 0, ok: false };
    try {
      const d = JSON.parse(dimensions);
      return {
        width: safeNum(d.width),
        height: safeNum(d.height),
        depth: safeNum(d.depth),
        ok: true
      };
    } catch {
      return { width: 0, height: 0, depth: 0, ok: false };
    }
  }

  function computeVolumeCuFtFromDims(width, height, depth) {
    return ((safeNum(width) * safeNum(height) * safeNum(depth)) / 1728);
  }

  return (
    <div style={pageContainer}>
      <button onClick={() => navigate(-1)}>← Back</button>

      <h5 style={{ margin: "10px 0" }}>
        Itemized Estimate #{lead.estimate_id}
      </h5>

      {/* ================= AUCTION COUNTDOWN ================= */}
      <div
        style={{
          marginBottom: 10,
          padding: 10,
          borderRadius: 8,
          background: timeLeftMs > 0 ? "#ecfeff" : "#fef2f2",
          border: "1px solid #67e8f9",
          fontSize: 18,
          fontWeight: "bold",
          textAlign: "center"
        }}
      >
        ⏱ {formatCountdown(timeLeftMs)}
      </div>

      {/* ================= PHOTOS ================= */}
      {images.length > 0 && (
        <div style={photoGrid}>
          {images.map((url, idx) => (
            <img
              key={idx}
              src={url}
              alt={`estimate-${idx}`}
              style={photo}
            />
          ))}
        </div>
      )}

      {/* ================= PAYMENT WARNING ================= */}
      {!hasVerifiedPayment && (
        <div style={paymentWarning}>
          <strong>Payment verification required:</strong>{" "}
          You may review this lead, but you must verify a payment method
          before placing a bid.
        </div>
      )}

      {/* ================= DISCLAIMER (TOP) ================= */}
      <div style={disclaimerBox}>
        <strong>Preliminary Estimate Disclaimer</strong>
        <p style={{ marginTop: 4, marginBottom: 0 }}>
          This estimate reflects the preliminary job price based on the
          information and images provided by the potential customer. The
          customer understands that the final price may be adjusted by the
          hauling professional during the in-person assessment if the scope,
          volume, weight, accessibility, or materials differ from what was
          originally submitted.
        </p>
      </div>

      {/* ================= ITEMS ACCORDION ================= */}
      <div style={card}>
        <h4 style={{ marginLeft: 12, marginTop: 10, marginBottom: 10 }}>
          Estimated Items
        </h4>

        {items.map((item, idx) => {
          const isOpen = openItemIndex === idx;

          const qty = safeNum(item.qty || 1);
          const itemTotal = safeNum(item.item_total);
          const weightFee = safeNum(item.weight_fee);
          const volumeFee = safeNum(item.volume_fee);
          const specialFee = safeNum(
            item.special_fee != null ? item.special_fee : item.surcharge_fee
          );

          const dims = parseDimensions(item.dimensions);
          const computedVolume = computeVolumeCuFtFromDims(
            dims.width,
            dims.height,
            dims.depth
          ).toFixed(1);

          const desc = (item.description || "").trim();
          const location = (item.location || "").trim();

          return (
            <div key={idx} style={accordionItem}>
              <div
                style={accordionHeader}
                onClick={() => setOpenItemIndex(isOpen ? null : idx)}
              >
                <strong>
                  {item.name} × {qty}
                </strong>
                <strong>${itemTotal.toFixed(2)}</strong>
              </div>

              {isOpen && (
                <div style={accordionDetails}>
                  <div style={descRow}>
                    <div style={colLabel}>Description:</div>
                    <div style={descValue}>
                      {desc || "No description available"}
                    </div>
                  </div>

                  <div style={descRow}>
                    <div style={colLabel}>Dimensions:</div>
                    <div style={descValue}>
                      {`${dims.width}w × ${dims.height}h × ${dims.depth}d`}
                    </div>
                  </div>

                  <div style={descRow}>
                    <div style={colLabel}>Location:</div>
                    <div style={descValue}>
                      {location || "Not specified"}
                    </div>
                  </div>

                  <DetailRow
                    label="Est. Volume:"
                    value={`${computedVolume} cu ft`}
                    price={volumeFee}
                  />

                  <DetailRow
                    label="Est. Weight:"
                    value={`~${safeNum(item.est_weight_lbs)} lbs`}
                    price={weightFee}
                  />

                  <DetailRow
                    label="Special Fee:"
                    value={specialFee ? "Yes" : "—"}
                    price={specialFee}
                  />

                  <div style={totalRow}>
                    <div style={detailTotal}>
                      Total for this item: ${itemTotal.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ================= TOTALS ================= */}
      <div style={summaryCard}>
        <SummaryRow label="Total Volume" value={`${totalVolume.toFixed(1)} cu ft`} />
        <SummaryRow label="Total Weight" value={`${totalWeight.toFixed(0)} lbs`} />
      </div>

      {/* ================= FEE BREAKDOWN ================= */}
      <div style={feeCard}>
        <h3 style={{ marginBottom: 10, marginTop: 0, textDecoration: "underline" }}>
          Fee Breakdown
        </h3>
        <FeeRow label="Base Fee" value={pricing.base_fee} />
        <FeeRow label="Dump Fee" value={pricing.dump_fee} />
        <FeeRow label="Weight Fees" value={pricing.weight_fee} />
        <FeeRow label="Surcharges" value={pricing.surcharge_fee} />
        <FeeRow label="Tax" value={pricing.tax} />
        <hr />
        <FeeRow label="TOTAL" value={pricing.grand_total} bold />
      </div>

      {/* ================= CUSTOMER CONTACT ================= */}
      <div style={contactCard}>
        <h4 style={{ marginBottom: 2, marginTop: 0 }}>Customer Contact Information:</h4>
        <div>Email: <em>Hidden until awarded</em></div>
        <div>Phone: <em>Hidden until awarded</em></div>
        <div>Address: <em>Hidden until awarded</em></div>
      </div>

      {/* ================= CLAIM / BID ================= */}
      <div style={claimSection}>
        <div style={exclusiveNotice}>
          <strong>Exclusive Lead:</strong> Only one vendor will ultimately be
          awarded this job.
        </div>

        <div style={noRefundNotice}>
          <strong>No Refund Policy:</strong> Bids are final. Payment is only
          captured if you are awarded the job.
        </div>

        <button
          onClick={() => navigate(`/bid-lead/${estimateId}`)}
          style={claimButton}
          disabled={timeLeftMs <= 0}
        >
          Bid on This Lead
        </button>

        {timeLeftMs <= 0 && (
          <div style={{ marginTop: 8, color: "#b91c1c", fontWeight: "bold" }}>
            This auction has ended.
          </div>
        )}

        {!hasVerifiedPayment && (
          <div style={bidHelperText}>
            You will be prompted to verify a payment method before placing a bid.
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= HELPERS ================= */

function DetailRow({ label, value, price, priceDash }) {
  return (
    <div style={detailRow}>
      <div style={colLabel}>{label}</div>
      <div style={colValue}>{value || "—"}</div>
      <div style={colPrice}>
        {priceDash ? "—" : `$${Number(price || 0).toFixed(2)}`}
      </div>
    </div>
  );
}

function FeeRow({ label, value, bold }) {
  return (
    <div style={feeRow}>
      <span style={{ fontWeight: bold ? "bold" : "normal" }}>{label}</span>
      <strong>${Number(value || 0).toFixed(2)}</strong>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={summaryRow}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/* ================= STYLES ================= */

const paymentWarning = {
  marginBottom: 10,
  padding: 10,
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  borderRadius: 6,
  color: "#7c2d12",
  fontSize: 14
};

const bidHelperText = {
  marginTop: 8,
  fontSize: 13,
  color: "#92400e"
};

const pageContainer = {
  padding: 10,
  maxWidth: 1100,
  margin: "0 auto",
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
};

/* (all remaining styles unchanged below) */

const disclaimerBox = {
  padding: 8,
  borderRadius: 8,
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#7c2d12",
  fontSize: 12,
  marginBottom: 10
};

const contactCard = {
  padding: 8,
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  background: "#f9fafb",
  marginBottom: 10,
  fontSize: 14
};

const photoGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: 12,
  marginBottom: 10
};

const photo = {
  width: "100%",
  height: 180,
  objectFit: "cover",
  borderRadius: 8,
  border: "1px solid #e5e7eb"
};

const card = {
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  overflow: "hidden"
};

const accordionItem = {
  borderBottom: "1px solid #e5e7eb"
};

const accordionHeader = {
  display: "flex",
  justifyContent: "space-between",
  padding: "14px 16px",
  cursor: "pointer",
  background: "#f9fafb"
};

const accordionDetails = {
  padding: "10px 12px",
  backgroundColor: "#f4f6fa",
  borderTop: "1px solid #e5e7eb"
};

const descRow = {
  display: "flex",
  alignItems: "flex-start",
  marginBottom: 6
};

const detailRow = {
  display: "flex",
  alignItems: "center",
  marginBottom: 6
};

const colLabel = {
  width: "33%",
  fontSize: 13,
  fontWeight: "600",
  color: "#444"
};

const descValue = {
  width: "77%",
  fontSize: 13,
  color: "#666",
  paddingLeft: 10
};

const colValue = {
  width: "50%",
  fontSize: 13,
  color: "#666",
  paddingLeft: 4
};

const colPrice = {
  width: "19%",
  fontSize: 13,
  fontWeight: "700",
  textAlign: "right",
  color: "#2563eb"
};

const totalRow = {
  marginTop: 10,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const detailTotal = {
  fontSize: 15,
  fontWeight: "700"
};

const feeCard = {
  marginTop: 10,
  marginBottom: 0,
  padding: 10,
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  background: "#fff"
};

const feeRow = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 4,
  fontSize: 15
};

const summaryCard = {
  marginTop: 10,
  marginBottom: 10,
  padding: 10,
  paddingBottom: 2,
  background: "#f1f5f9",
  borderRadius: 8,
  fontWeight: "bold"
};

const summaryRow = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 12,
  fontSize: 16
};

const claimSection = {
  marginTop: 0,
  padding: 12,
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  background: "#fafafa"
};

const exclusiveNotice = {
  marginBottom: 8,
  fontSize: 14,
  color: "#1e3a8a"
};

const noRefundNotice = {
  marginBottom: 12,
  fontSize: 14,
  color: "#b91c1c"
};

const claimButton = {
  background: "#2563eb",
  color: "#fff",
  padding: "14px 24px",
  fontSize: 16,
  border: "none",
  borderRadius: 6,
  cursor: "pointer"
};
